import React from 'react';
import { Search, MousePointerClick, Percent, Flag } from 'lucide-react';
import { usePanelSearchFunnel } from '../../lib/api';
import { DateRange } from '../../lib/panelTypes';
import StatCard from './StatCard';

const num = (n: number) => (n || 0).toLocaleString();

const SearchFunnelTab: React.FC<{ range: DateRange }> = ({ range }) => {
  const { data, isLoading } = usePanelSearchFunnel(range);
  const searches = data?.searches ?? 0;
  const clicks = data?.resultClicks ?? 0;
  const sessions = data?.sessions ?? 0;
  const outcomes = data?.outcomes ?? [];
  const maxOutcome = Math.max(1, ...outcomes.map((o) => o.count));

  const funnelRows = [
    { label: 'Searches', value: searches },
    { label: 'Result clicks', value: clicks },
    { label: 'Sessions completed', value: sessions },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Searches" value={num(searches)} icon={Search} />
        <StatCard label="Result clicks" value={num(clicks)} icon={MousePointerClick} />
        <StatCard label="Click-through rate" value={`${(data?.clickThroughRate ?? 0).toFixed(1)}%`} icon={Percent} hint="clicks / searches" />
        <StatCard label="Sessions completed" value={num(sessions)} icon={Flag} />
      </div>

      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Search → click → outcome</h3>
        {searches === 0 ? (
          <p className="text-sm text-gray-400">No searches in this range.</p>
        ) : (
          <div className="space-y-3">
            {funnelRows.map((row) => (
              <div key={row.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{row.label}</span>
                  <span className="text-gray-500">
                    {num(row.value)} · {searches > 0 ? ((row.value / searches) * 100).toFixed(0) : 0}%
                  </span>
                </div>
                <div className="h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-cyan-500 rounded"
                    style={{ width: `${searches > 0 ? Math.max(2, (row.value / searches) * 100) : 0}%` }}
                  />
                </div>
              </div>
            ))}
            {clicks === 0 && (
              <p className="text-xs text-gray-400 pt-1">
                Result clicks &amp; session outcomes populate once the updated app is live.
              </p>
            )}
          </div>
        )}
      </div>

      {outcomes.length > 0 && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Session outcomes</h3>
          <div className="space-y-2">
            {outcomes.map((o) => (
              <div key={o.outcome}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 capitalize">{o.outcome}</span>
                  <span className="text-gray-500">{num(o.count)}</span>
                </div>
                <div className="h-4 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full bg-green-500 rounded" style={{ width: `${(o.count / maxOutcome) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
    </div>
  );
};

export default SearchFunnelTab;
